import React, { useState, useEffect, useMemo } from 'react'
import {
  Collapse,
  Tooltip,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import {
  ListItemButton as MuiListItemButton,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useAuth0 } from '@auth0/auth0-react'
import { Drawer } from '@/components/UI/Drawer'
import {
  BarChartIcon,
  ParentIcon,
  HomeIcon,
  CheckDoneIcon,
  ExpandMoreIcon,
  ExpandLessIcon,
  LogoutIcon,
} from '@/components/UI/Icons'
import { clearCurrentUser } from '@/db/indexedDB'
import { usePermissions } from '@/domain/permissions/usePermissions'
import { PlatformPermission } from '@/domain/permissions/rbac'
import { ENV_MODES } from '@/utils/constants'
import { useNavigate } from 'react-router-dom'
import ScienceIcon from '@mui/icons-material/Science'
import InventoryIcon from '@mui/icons-material/Inventory2Outlined'

interface MenuItem {
  label: string
  icon: React.ReactElement | null
  children?: MenuItem[]
  // Items are placeholders until a page exists for them; `enabled` opts an
  // item into being clickable and routed.
  enabled?: boolean
}

interface SidebarProps {
  onSelect: (label: string) => void
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  activePage: string
  onToggleSidebar: () => void
}

const menuItems: MenuItem[] = [
  { label: 'Dashboard', icon: <BarChartIcon /> },
  { label: 'Institutions', icon: <HomeIcon />, enabled: true },
  { label: 'Products', icon: <InventoryIcon fontSize="small" />, enabled: true },
  {
    label: 'Incidents',
    icon: <CheckDoneIcon />,
    children: [
      { label: 'Failed KYC', icon: null },
      { label: 'Failed Direct Debit', icon: null },
      { label: 'Failed Loans', icon: null },
    ],
  },
]

export const Sidebar: React.FC<SidebarProps> = ({ onSelect, open, setOpen, activePage, onToggleSidebar }) => {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({})
  const [activeLabel, setActiveLabel] = useState<string | null>(null)
  const { user, logout } = useAuth0()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const { hasPermission } = usePermissions(user?.['https://carringtonlabs.io/appRole'])
  const canViewUsers = hasPermission(PlatformPermission.ViewUser)
  // Institutions is Support-only. Admin also passes, since hasPermission treats
  // Admin and Support as super-roles. Products is deliberately ungated.
  const canViewInstitutions = hasPermission(PlatformPermission.Support)
  const isDevEnv =
    import.meta.env.MODE === ENV_MODES.DEVELOPMENT ||
    import.meta.env.MODE === ENV_MODES.STAGING

  useEffect(() => {
    if (activePage) {
      setActiveLabel(activePage)
    }
  }, [activePage])

  useEffect(() => {
    if (!open) {
      setOpenItems({})
    }
  }, [open])

  const transformedMenuItems = useMemo(() => {
    const items = menuItems.filter(
      item => item.label !== 'Institutions' || canViewInstitutions
    )

    if (canViewUsers) {
      items.splice(1, 0, {
        label: 'Customers',
        icon: <ParentIcon />,
        enabled: true,
      })
    }

    return items
  }, [canViewUsers, canViewInstitutions])

  const handleDeveloperClick = () => {
    setActiveLabel('Developer')
    onSelect('Developer')
    navigate('/developer')
    if (isMobile) setOpen(false)
  }

  const toggleItem = (label: string) => {
    setOpenItems(prev => ({
      ...prev,
      [label]: !prev[label],
    }))
  }

  const MENU_ROUTES: Record<string, string> = {
    Customers: '/app',
    Institutions: '/institutions',
    Products: '/products',
  }

  const handleItemClick = (label: string, hasChildren: boolean, isChild: boolean = false) => {
    if (hasChildren && !isChild) {
      toggleItem(label)
    } else {
      setActiveLabel(label)
      onSelect(label)
      if (MENU_ROUTES[label]) navigate(MENU_ROUTES[label])
      if (isMobile) setOpen(false)
    }
  }

  const handleLogout = () => {
    clearCurrentUser()
    localStorage.clear()
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    })
  }

  const renderMenu = (items: MenuItem[]) =>
    items.map(item => {
      const hasChildren = Boolean(item.children)
      const isSelected = activeLabel === item.label

      const isDisabled = !item.enabled

      const Button = (
        <ListItemButton
          disabled={isDisabled}
          selected={isSelected}
          onClick={() => !isDisabled && handleItemClick(item.label, hasChildren)}
          sx={{ justifyContent: open ? 'initial' : 'center' }}
        >
          <ListItemIcon sx={{ mr: open ? 3 : 'auto', opacity: isDisabled ? 0.4 : 1 }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            sx={{
              opacity: open ? 1 : 0,
              color: isDisabled ? '#A0A0A0' : 'inherit',
            }}
          />
          {hasChildren && open && (openItems[item.label] ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
        </ListItemButton>
      )

      return (
        <React.Fragment key={item.label}>
          {open ? (
            Button
          ) : (
            <Tooltip title={item.label} placement="right">
              {Button}
            </Tooltip>
          )}
          {hasChildren && (
            <Collapse in={openItems[item.label]} timeout="auto" unmountOnExit>
              {item.children!.map(child => (
                <ListItemButton
                  key={child.label}
                  disabled={isMobile}
                  onClick={() => handleItemClick(child.label, false, true)}
                  selected={activeLabel === child.label}
                  sx={{
                    pl: open ? 5.5 : 2,
                    justifyContent: open ? 'initial' : 'center',
                  }}
                >
                  <ListItemIcon sx={{ mr: open ? 3 : 'auto' }}>{child.icon}</ListItemIcon>
                  <ListItemText primary={child.label} sx={{ opacity: open ? 1 : 0 }} />
                </ListItemButton>
              ))}
            </Collapse>
          )}
        </React.Fragment>
      )
    })

  return (
    <>
      {isMobile && (
        <AppBar position="fixed" sx={{ zIndex: theme => theme.zIndex.drawer + 1 }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => setOpen(!open)}>
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      <Drawer
        open={open}
        onClose={isMobile ? () => setOpen(false) : onToggleSidebar}
        variant={isMobile ? 'temporary' : 'permanent'}
        sx={{
          width: open ? 240 : 72,
          '& .MuiDrawer-paper': {
            width: open ? 240 : 72,
            ...(isMobile &&
              !open && {
                border: 'none',
                backgroundColor: 'transparent',
              }),
          },
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>{renderMenu(transformedMenuItems)}</Box>

          {/* Developer Tools — only in dev / UAT */}
          {isDevEnv && (
            <Box sx={{ borderTop: '1px dashed #c7d7ff', mx: 1, mt: 1, pt: 1 }}>
              {open ? (
                <ListItemButton
                  selected={activeLabel === 'Developer'}
                  onClick={handleDeveloperClick}
                  sx={{ justifyContent: 'initial' }}
                >
                  <ListItemIcon sx={{ mr: 3, color: '#4C4CFF' }}>
                    <ScienceIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Developer"
                    sx={{ opacity: 1, '& .MuiTypography-root': { fontSize: 14, fontWeight: 600, color: '#4C4CFF' } }}
                  />
                </ListItemButton>
              ) : (
                <Tooltip title="Developer Tools" placement="right">
                  <ListItemButton
                    selected={activeLabel === 'Developer'}
                    onClick={handleDeveloperClick}
                    sx={{ justifyContent: 'center' }}
                  >
                    <ListItemIcon sx={{ mr: 'auto', color: '#4C4CFF' }}>
                      <ScienceIcon fontSize="small" />
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              )}
            </Box>
          )}

          <Box sx={{ borderTop: '1px solid #e0e0e0', mt: 1, pt: 1 }}>
            {open ? (
              <ListItemButton onClick={() => handleLogout()} sx={{ justifyContent: 'initial' }}>
                <ListItemIcon sx={{ mr: 3 }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Logout" sx={{ opacity: 1 }} />
              </ListItemButton>
            ) : (
              <Tooltip title="Logout" placement="right">
                <ListItemButton onClick={() => handleLogout()} sx={{ justifyContent: 'center' }}>
                  <ListItemIcon sx={{ mr: 'auto' }}>
                    <LogoutIcon />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            )}
          </Box>
        </div>
      </Drawer>
    </>
  )
}

const ListItemButton = styled(MuiListItemButton)(({ theme }) => ({
  minHeight: 48,
  justifyContent: 'flex-start',
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
  color: '#344054',
  borderRadius: '10px',
  transition: 'color 0.3s, background-color 0.3s',
  '&:hover': {
    backgroundColor: '#ECEEFC',
    color: '#4C4CFF',
  },
  '&.Mui-selected': {
    backgroundColor: '#ECEEFC',
    color: '#4C4CFF',
    fontWeight: 600,
  },
}))

const ListItemIcon = styled(MuiListItemIcon)(({ theme }) => ({
  minWidth: 0,
  marginRight: theme.spacing(3),
  justifyContent: 'center',
  color: 'inherit',
}))

const ListItemText = styled(MuiListItemText)(() => ({
  transition: 'opacity 0.3s',
}))
